import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { AppButton, AppFileButton, AppMenuItem, AppSelect, AppTextField, AppToggleField } from "../../../components/form";
import {
  AppFormField,
  AppFormGrid,
  PageSectionCard,
} from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import {
  GRID_FULL,
  GRID_HALF,
  GRID_THIRD,
  GRID_TWO_THIRDS,
} from "../../../lib/ui/formLayout";
import {
  ingestService,
  type CsvDelimiter,
  type IngestEnvironment,
  type IngestJobResponse,
} from "../../../services/ingestService";
import { workspaceService } from "../../../services/workspaceService";
import { getAccessToken } from "../../../api/api";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import {
  resolveWorkspaceIndexName,
  validateOpenSearchIndexName,
  validateOpenSearchIndexShortName,
  workspaceIndexPrefix,
} from "../../../utils/openSearchIndexName";
import IngestJobDetailPanel from "../components/IngestJobDetailPanel";

/** Sentinel option in the target-index autocomplete for creating a new index. */
const NEW_INDEX_OPTION = "__new_index__";

function canDeleteIngestJob(job: IngestJobResponse): boolean {
  if (!job.id) return false;
  const status = String(job.status ?? "").toUpperCase();
  return status !== "PROCESSING";
}

function isSuccessfulIngestJob(job: IngestJobResponse): boolean {
  const status = String(job.status ?? "").toUpperCase();
  return status === "COMPLETED" || status === "PARTIALLY_COMPLETED";
}

function canPromoteIngestJob(job: IngestJobResponse): boolean {
  return Boolean(job.id) && job.environment === "STAGING" && isSuccessfulIngestJob(job);
}

function canAnalyzeIngestJob(job: IngestJobResponse): boolean {
  return Boolean(job.id) && job.environment === "PRODUCTION" && isSuccessfulIngestJob(job);
}

function isCsvFile(file: File | null): boolean {
  return Boolean(file && file.name.toLowerCase().endsWith(".csv"));
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function resultAlertSeverity(status: string): "success" | "warning" | "error" | "info" {
  const normalized = String(status ?? "").toUpperCase();
  if (normalized === "COMPLETED") return "success";
  if (normalized === "PARTIALLY_COMPLETED") return "warning";
  if (normalized === "FAILED") return "error";
  if (normalized === "PROCESSING" || normalized === "PENDING") return "info";
  return "warning";
}

export default function IngestUploadPage() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const confirm = useConfirm();
  const [index, setIndex] = useState("");
  /** Editable suffix when creating a new index (prefix comes from workspace settings). */
  const [indexShortName, setIndexShortName] = useState("");
  const [createNewIndex, setCreateNewIndex] = useState(false);
  const [indexPrefix, setIndexPrefix] = useState<string | null>(null);
  const [indices, setIndices] = useState<string[]>([]);
  const [indicesLoading, setIndicesLoading] = useState(true);
  const [environment, setEnvironment] = useState<IngestEnvironment>("STAGING");
  const [csvDelimiter, setCsvDelimiter] = useState<CsvDelimiter>("AUTO");
  const [bulkBatchSize, setBulkBatchSize] = useState("50");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actingJobId, setActingJobId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<IngestJobResponse | null>(null);
  const [jobs, setJobs] = useState<IngestJobResponse[]>([]);
  const [selectedJob, setSelectedJob] = useState<IngestJobResponse | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadJobs = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) return;
    try {
      const list = await ingestService.listJobs();
      setJobs(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadIndices = useCallback(async () => {
    setIndicesLoading(true);
    try {
      const res = await ingestService.listIndices();
      const list = res.indices ?? [];
      setIndices(list);
      setIndex((prev) => {
        if (!prev) return list[0] || "";
        return prev;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIndicesLoading(false);
    }
  }, []);

  const loadIndexPrefix = useCallback(async () => {
    try {
      const settings = await workspaceService.getSettings();
      // Prefer stored BEAS prefix; always fall back to ws-<workspaceId>- so New index never lacks a pattern.
      const fromSettings = settings.openSearchIndexPrefix?.trim() || null;
      const fromWorkspace = workspaceIndexPrefix(settings.workspaceId);
      setIndexPrefix(fromSettings || fromWorkspace);
    } catch (e) {
      console.error(e);
      setIndexPrefix(null);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const boot = async () => {
      const token = await getAccessToken();
      if (!active || !token) return;
      await Promise.all([loadJobs(), loadIndices(), loadIndexPrefix()]);
    };
    boot().catch(console.error);
    return () => {
      active = false;
    };
  }, [loadJobs, loadIndices, loadIndexPrefix]);

  const hasInFlightJobs = useMemo(
    () =>
      jobs.some((job) => {
        const status = String(job.status ?? "").toUpperCase();
        return status === "PROCESSING" || status === "PENDING";
      }),
    [jobs]
  );

  // Keep the jobs table fresh while uploads run in the background.
  useEffect(() => {
    if (!hasInFlightJobs) {
      return;
    }
    const timer = window.setInterval(() => {
      loadJobs().catch(console.error);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [hasInFlightJobs, loadJobs]);

  // Reflect background job progress in the last-result banner.
  useEffect(() => {
    if (!lastResult?.id) {
      return;
    }
    const updated = jobs.find((job) => job.id === lastResult.id);
    if (!updated) {
      return;
    }
    if (
      updated.status !== lastResult.status ||
      updated.indexedCount !== lastResult.indexedCount ||
      updated.rowCount !== lastResult.rowCount ||
      updated.errorCount !== lastResult.errorCount ||
      updated.progress !== lastResult.progress
    ) {
      setLastResult(updated);
    }
  }, [jobs, lastResult]);

  useEffect(() => {
    if (!selectedJob?.id) {
      return;
    }
    const updated = jobs.find((job) => job.id === selectedJob.id);
    if (!updated) {
      return;
    }
    if (
      updated.status !== selectedJob.status ||
      updated.indexedCount !== selectedJob.indexedCount ||
      updated.rowCount !== selectedJob.rowCount ||
      updated.errorCount !== selectedJob.errorCount ||
      updated.progress !== selectedJob.progress
    ) {
      setSelectedJob(updated);
    }
  }, [jobs, selectedJob]);

  const hadInFlightJobs = useRef(false);
  // Refresh index picker once background jobs finish.
  useEffect(() => {
    if (hasInFlightJobs) {
      hadInFlightJobs.current = true;
      return;
    }
    if (hadInFlightJobs.current) {
      hadInFlightJobs.current = false;
      loadIndices().catch(console.error);
    }
  }, [hasInFlightJobs, loadIndices]);

  const indexOptions = useMemo(() => [NEW_INDEX_OPTION, ...indices], [indices]);

  const resolvedNewIndexName = useMemo(
    () => (createNewIndex ? resolveWorkspaceIndexName(indexShortName, indexPrefix) : ""),
    [createNewIndex, indexShortName, indexPrefix]
  );

  const indexNameCheck = useMemo(() => {
    if (!createNewIndex) return { ok: Boolean(index.trim()) };
    if (indexPrefix) return validateOpenSearchIndexShortName(indexShortName);
    return validateOpenSearchIndexName(indexShortName);
  }, [createNewIndex, index, indexPrefix, indexShortName]);

  const indexHelperText = createNewIndex
    ? t("ingest.targetIndexAutoCreateHint")
    : t("ingest.targetIndexHint");

  const indexNameErrorText = useMemo(() => {
    if (!createNewIndex || !indexShortName.trim() || indexNameCheck.ok) return null;
    const code = "error" in indexNameCheck ? indexNameCheck.error : undefined;
    switch (code) {
      case "uppercase":
        return t("ingest.indexNameError.uppercase");
      case "tooLong":
        return t("ingest.indexNameError.tooLong");
      case "reserved":
        return t("ingest.indexNameError.reserved");
      case "invalidChars":
        return indexPrefix
          ? t("ingest.indexNameError.shortInvalidChars")
          : t("ingest.indexNameError.invalidChars");
      case "empty":
        return t("ingest.indexNameError.empty");
      default:
        return t("ingest.indexNameError.invalidChars");
    }
  }, [createNewIndex, indexShortName, indexNameCheck, indexPrefix, t]);

  const parsedBulkBatchSize = useMemo(() => {
    const value = parseInt(bulkBatchSize, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }, [bulkBatchSize]);

  const onUpload = async () => {
    const target = createNewIndex ? resolvedNewIndexName : index.trim();
    if (!file || parsedBulkBatchSize <= 0 || !target) return;
    if (createNewIndex && !indexNameCheck.ok) return;
    if (createNewIndex && environment !== "STAGING") {
      setError(t("ingest.newIndexStagingOnly"));
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const queued = await ingestService.uploadFile(
        file,
        target,
        environment,
        parsedBulkBatchSize,
        isCsvFile(file) ? csvDelimiter : "AUTO"
      );
      setLastResult(queued);
      setFile(null);
      setIndexShortName("");
      await loadJobs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const deleteJob = async (job: IngestJobResponse) => {
    try {
      await confirmDelete({ itemName: job.sourceFileName || job.targetIndex });
      await ingestService.deleteJob(job.id);
      await loadJobs();
      if (lastResult?.id === job.id) {
        setLastResult(null);
      }
      if (selectedJob?.id === job.id) {
        setDetailOpen(false);
        setSelectedJob(null);
      }
    } catch (error) {
      if (isCancelled(error)) return;
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const confirmJobAction = async (title: string, description: string) => {
    await confirm({
      title,
      description,
      confirmationText: t("common.yes"),
      cancellationText: t("common.cancel"),
    });
  };

  const promoteJob = async (job: IngestJobResponse) => {
    try {
      await confirmJobAction(t("ingest.promoteTitle"), t("ingest.promoteConfirm", { index: job.targetIndex }));
    } catch {
      return;
    }
    setActingJobId(job.id);
    setError(null);
    setNotice(null);
    try {
      await ingestService.promoteToProduction(job.id);
      setNotice(t("ingest.promoteSuccess", { index: job.targetIndex }));
      await loadJobs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActingJobId(null);
    }
  };

  const analyzeJob = async (job: IngestJobResponse) => {
    try {
      await confirmJobAction(t("ingest.analyzeTitle"), t("ingest.analyzeConfirm", { index: job.targetIndex }));
    } catch {
      return;
    }
    setActingJobId(job.id);
    setError(null);
    setNotice(null);
    try {
      await ingestService.analyzeForMatchingTask(job.id);
      setNotice(t("ingest.analyzeSuccess", { index: job.targetIndex }));
      await loadJobs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActingJobId(null);
    }
  };

  const openJobDetail = async (job: IngestJobResponse) => {
    setSelectedJob(job);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const full = await ingestService.getJob(job.id);
      setSelectedJob(full);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const canUpload = Boolean(
    file &&
      (createNewIndex ? indexShortName.trim() && indexPrefix : index.trim()) &&
      indexNameCheck.ok &&
      !uploading &&
      parsedBulkBatchSize > 0 &&
      !(createNewIndex && environment !== "STAGING")
  );

  return (
    <PageListContainer surface="plain">
      <PageHeader title={t("ingest.title")} subtitle={t("ingest.subtitle")} />

      <PageSectionCard title={t("ingest.uploadSection")}>
        <Stack spacing={2}>
          <Stack spacing={0.75}>
            <AppFormGrid spacing="compact" alignItems="flex-end">
              <AppFormField size={GRID_TWO_THIRDS} align="end">
                <Autocomplete
                  options={indexOptions}
                  value={createNewIndex ? NEW_INDEX_OPTION : index || null}
                  loading={indicesLoading}
                  disableClearable={false}
                  onChange={(_, value) => {
                    if (value == null) {
                      setCreateNewIndex(false);
                      setIndex("");
                      setIndexShortName("");
                      return;
                    }
                    if (value === NEW_INDEX_OPTION) {
                      setCreateNewIndex(true);
                      setIndexShortName("");
                      setEnvironment("STAGING");
                      return;
                    }
                    setCreateNewIndex(false);
                    setIndexShortName("");
                    setIndex(value);
                  }}
                  getOptionLabel={(option) =>
                    option === NEW_INDEX_OPTION ? t("ingest.newIndex") : option
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {option === NEW_INDEX_OPTION ? (
                        <Chip size="small" color="primary" variant="outlined" label={t("ingest.newIndex")} />
                      ) : (
                        option
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <AppTextField {...params} label={t("ingest.targetIndex")} />
                  )}
                />
              </AppFormField>
              <AppFormField size={GRID_THIRD} align="end">
                <AppToggleField
                  hideLabel
                  value={environment}
                  onChange={(value) => setEnvironment(value as IngestEnvironment)}
                  options={[
                    { value: "STAGING", label: t("ingest.staging") },
                    { value: "PRODUCTION", label: t("ingest.production"), disabled: createNewIndex },
                  ]}
                />
              </AppFormField>
            </AppFormGrid>
            {createNewIndex ? (
              <AppFormGrid spacing="compact" alignItems="flex-start">
                {indexPrefix ? (
                  <AppFormField size={GRID_HALF} align="start">
                    <AppTextField
                      label={t("ingest.newIndexPrefixField")}
                      value={indexPrefix}
                      disabled
                      InputProps={{ readOnly: true }}
                      helperText={t("ingest.newIndexPrefixHint")}
                      inputProps={{
                        spellCheck: false,
                        sx: { fontFamily: "monospace", fontSize: 13 },
                      }}
                    />
                  </AppFormField>
                ) : (
                  <AppFormField size={GRID_FULL} align="start">
                    <Typography variant="caption" color="warning.main">
                      {t("ingest.newIndexPrefixMissing")}
                    </Typography>
                  </AppFormField>
                )}
                <AppFormField size={indexPrefix ? GRID_HALF : GRID_FULL} align="start">
                  <AppTextField
                    label={t("ingest.newIndexName")}
                    value={indexShortName}
                    onChange={(e) => {
                      const next = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
                      setIndexShortName(next);
                    }}
                    error={Boolean(indexNameErrorText)}
                    helperText={
                      indexNameErrorText ||
                      (indexPrefix
                        ? t("ingest.newIndexNameHintWithPrefix", {
                            full: resolvedNewIndexName || `${indexPrefix}…`,
                          })
                        : t("ingest.newIndexNameHint"))
                    }
                    autoFocus
                    inputProps={{ spellCheck: false, autoCapitalize: "off", autoCorrect: "off" }}
                  />
                </AppFormField>
              </AppFormGrid>
            ) : null}
            <AppFormGrid spacing="compact" alignItems="flex-start">
              <AppFormField size={isCsvFile(file) ? GRID_HALF : GRID_THIRD} align="start">
                <AppTextField
                  label={t("ingest.bulkBatchSize")}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={bulkBatchSize}
                  onChange={(e) => setBulkBatchSize(e.target.value)}
                  helperText={t("ingest.bulkBatchSizeHint")}
                />
              </AppFormField>
              {isCsvFile(file) ? (
                <AppFormField size={GRID_HALF} align="start">
                  <AppSelect
                    label={t("ingest.csvDelimiter")}
                    helperText={t("ingest.csvDelimiterHint")}
                    value={csvDelimiter}
                    onChange={(event) => setCsvDelimiter(String(event.target.value) as CsvDelimiter)}
                  >
                    <AppMenuItem value="AUTO">{t("ingest.csvDelimiterAuto")}</AppMenuItem>
                    <AppMenuItem value="COMMA">{t("ingest.csvDelimiterComma")}</AppMenuItem>
                    <AppMenuItem value="SEMICOLON">{t("ingest.csvDelimiterSemicolon")}</AppMenuItem>
                    <AppMenuItem value="TAB">{t("ingest.csvDelimiterTab")}</AppMenuItem>
                  </AppSelect>
                </AppFormField>
              ) : null}
            </AppFormGrid>
            <Typography variant="caption" color="text.secondary" sx={custom.pages.shared.captionBlockLineHeight}>
              {indicesLoading ? t("ingest.loadingIndices") : indexHelperText}
            </Typography>
            {createNewIndex && environment !== "STAGING" ? (
              <Typography variant="caption" color="error" sx={custom.pages.shared.captionBlockLineHeight}>
                {t("ingest.newIndexStagingOnly")}
              </Typography>
            ) : null}
          </Stack>

          <Box sx={custom.pages.ingest.uploadPanel}>
            <AppFileButton
              variant="outlined"
              color="secondary"
              startIcon={<CloudUploadOutlinedIcon />}
              accept=".csv,.xlsx,.xls"
              onFileSelected={(f) => setFile(f)}
              sx={custom.pages.ingest.fileButton}
            >
              {t("ingest.chooseFile")}
            </AppFileButton>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={custom.pages.ingest.fileInfoStack}
            >
              <InsertDriveFileOutlinedIcon
                fontSize="small"
                color={file ? "primary" : "disabled"}
                sx={custom.pages.shared.flexShrinkZero}
              />
              <Box sx={custom.pages.shared.minWidthZero}>
                <Typography
                  variant="body2"
                  fontWeight={file ? 600 : 400}
                  color={file ? "text.primary" : "text.secondary"}
                  noWrap
                >
                  {file ? file.name : t("ingest.fileHint")}
                </Typography>
                {file ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <AppButton
              variant="contained"
              color="primary"
              onClick={onUpload}
              disabled={!canUpload}
              sx={custom.pages.ingest.uploadButton}
            >
              {uploading ? t("common.loading") : t("ingest.upload")}
            </AppButton>
          </Box>
        </Stack>
      </PageSectionCard>

      {error && <Alert severity="error" sx={custom.pages.shared.alertMarginBottom2}>{error}</Alert>}
      {notice && (
        <Alert severity="success" sx={custom.pages.shared.alertMarginBottom2} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {lastResult && (
        <>
          <Alert severity={resultAlertSeverity(lastResult.status)} sx={custom.pages.shared.alertMarginBottom2}>
            {["PROCESSING", "PENDING"].includes(String(lastResult.status ?? "").toUpperCase())
              ? t("ingest.jobLive", {
                  index: lastResult.targetIndex,
                  step: lastResult.progress
                    ? t(`ingest.progress.${lastResult.progress}`, { defaultValue: lastResult.progress })
                    : t("ingest.progress.starting"),
                  indexed: lastResult.indexedCount,
                  total: lastResult.rowCount,
                })
              : t("ingest.resultSummary", {
                  indexed: lastResult.indexedCount,
                  failed: lastResult.errorCount,
                  total: lastResult.rowCount,
                  status: lastResult.status,
                })}
          </Alert>
          {lastResult.failedRecords && lastResult.failedRecords.length > 0 && (
            <Box sx={custom.pages.shared.alertMarginBottom2}>
            <PageSectionCard
              title={t("ingest.failureReportTitle", { count: lastResult.errorCount })}
              noPadding
            >
              <Box sx={custom.pages.shared.overflowAuto}>
                <Table size="small" sx={custom.pages.shared.tableMinWidth720}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("ingest.colFailureRow")}</TableCell>
                      <TableCell>{t("ingest.colFailureColumn")}</TableCell>
                      <TableCell>{t("ingest.colFailureType")}</TableCell>
                      <TableCell>{t("ingest.colFailureReason")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lastResult.failedRecords.map((failure, index) => (
                      <TableRow key={`${failure.rowNumber}-${failure.column ?? index}`}>
                        <TableCell>{failure.rowNumber}</TableCell>
                        <TableCell>{failure.column || "—"}</TableCell>
                        <TableCell>{failure.errorType || "—"}</TableCell>
                        <TableCell>{failure.errorReason || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {lastResult.errorCount > lastResult.failedRecords.length && (
                <Typography variant="caption" color="text.secondary" sx={custom.pages.shared.captionBlockLineHeight}>
                  {t("ingest.failureReportTruncated", {
                    shown: lastResult.failedRecords.length,
                    total: lastResult.errorCount,
                  })}
                </Typography>
              )}
            </PageSectionCard>
            </Box>
          )}
        </>
      )}

      <PageSectionCard
        title={t("ingest.recentJobs")}
        actions={
          <AppButton variant="outlined" size="small" onClick={() => loadJobs().catch(console.error)}>
            {t("common.refresh")}
          </AppButton>
        }
        noPadding={jobs.length > 0}
      >
        {jobs.length === 0 ? (
          <PageEmptyState message={t("ingest.noJobs")} />
        ) : (
        <Box sx={custom.pages.shared.overflowAuto}>
          <Table size="small" sx={custom.pages.shared.tableMinWidth720}>
          <TableHead>
            <TableRow>
              <TableCell>{t("ingest.colFile")}</TableCell>
              <TableCell>{t("ingest.colIndex")}</TableCell>
              <TableCell>{t("ingest.colEnv")}</TableCell>
              <TableCell>{t("ingest.colStatus")}</TableCell>
              <TableCell align="right">{t("ingest.colRows")}</TableCell>
              <TableCell align="right" sx={custom.pages.shared.tableActionsCell}>
                {t("common.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => openJobDetail(job)}
              >
                <TableCell>{job.sourceFileName || "—"}</TableCell>
                <TableCell>{job.targetIndex}</TableCell>
                <TableCell>
                  <Chip size="small" label={job.environment} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.status}</Typography>
                  {job.progress && (job.status === "PROCESSING" || job.status === "PENDING") ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t(`ingest.progress.${job.progress}`, { defaultValue: job.progress })}
                      {job.rowCount > 0 ? ` · ${job.indexedCount}/${job.rowCount}` : ""}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  {job.indexedCount}/{job.rowCount}
                  {job.errorCount > 0 ? ` (${job.errorCount} ${t("ingest.failedShort")})` : ""}
                </TableCell>
                <TableCell align="right" sx={custom.pages.shared.tableActionsCell}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {canPromoteIngestJob(job) ? (
                      <Tooltip title={t("ingest.promote")}>
                        <span>
                          <AppIconButton
                            size="small"
                            aria-label={t("ingest.promote")}
                            disabled={actingJobId === job.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void promoteJob(job);
                            }}
                          >
                            <PublishOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                    {canAnalyzeIngestJob(job) ? (
                      <Tooltip title={t("ingest.analyze")}>
                        <span>
                          <AppIconButton
                            size="small"
                            aria-label={t("ingest.analyze")}
                            disabled={actingJobId === job.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void analyzeJob(job);
                            }}
                          >
                            <InsightsOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                    {canDeleteIngestJob(job) ? (
                      <Tooltip title={t("ingest.deleteJob")}>
                        <span>
                          <AppIconButton
                            size="small"
                            color="error"
                            aria-label={t("ingest.deleteJob")}
                            disabled={actingJobId === job.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteJob(job);
                            }}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
        )}
      </PageSectionCard>

      <IngestJobDetailPanel
        open={detailOpen}
        job={selectedJob}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setSelectedJob(null);
        }}
      />
    </PageListContainer>
  );
}
